/**
 * Single source of truth for the browser-side impersonation flag.
 *
 * An admin picks a target and the app answers as them: their events, their
 * trackers, their account — always at normal-user permission level. The target's
 * id is persisted to localStorage under IMPERSONATE_USER_STORAGE_KEY, the
 * api-client mirrors it onto every request as the IMPERSONATE_USER_HEADER (see
 * api-client.ts), and the gears-api authentication middleware swaps the
 * request-scoped user for that target. It never touches the stored/session admin
 * flag, so switching back is immediate.
 *
 * All access to the flag goes through this module so the storage key and the
 * private-mode/quota try/catch guard live in exactly one place. Every function is
 * browser-only: it no-ops (or returns null) when window / localStorage is
 * unavailable, so the server-side api-client never sends the header.
 */
export const IMPERSONATE_USER_STORAGE_KEY = 'racemap:impersonateUser';

export function getImpersonatedUserId(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const userId = window.localStorage.getItem(IMPERSONATE_USER_STORAGE_KEY);
    return userId != null && userId !== '' ? userId : null;
  } catch {
    return null;
  }
}

export function activateImpersonation(userId: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(IMPERSONATE_USER_STORAGE_KEY, userId);
  } catch {
    // ignore — localStorage may be unavailable (private mode/quota)
  }
}

export function deactivateImpersonation(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(IMPERSONATE_USER_STORAGE_KEY);
  } catch {
    // ignore — localStorage may be unavailable (private mode/quota)
  }
}
